import type { RootState } from '@/store';

export const selectAuthToken = (state: RootState) => state.auth?.jwtToken;
export const selectUserId = (state: RootState): number => state.auth?.userId || 0;
export const selectUserActiveGroupId = (state: RootState): number =>
  state.auth.tokenVerification?.pvActiveGroupID || 1;
export const isAdministratorSelector = (state: RootState) =>
  state.auth.tokenVerification?.pvAdministrator === 1;
export const tokenVerificationSelector = (state: RootState) => state.auth.tokenVerification;
export const groupAccountSelector = (state: RootState) => {
  const groupId = state.auth?.tokenVerification?.pvActiveGroupID || 0;
  const currentGroups = state.auth?.tokenVerification?.currentGroups || [];
  return currentGroups.find((group) => +group.i === +groupId) || null;
};
export const userActiveGroupSelector = (state: RootState) =>
  state.auth.tokenVerification?.pvActiveGroupID || 0;

export const groupNameSelector = (state: RootState): string => {
  const groupId = state.auth?.tokenVerification?.pvActiveGroupID || 0;
  const currentGroups = state.auth?.tokenVerification?.currentGroups || [];
  const group = currentGroups.find((g) => +g.i === +groupId);
  return group?.n?.toLowerCase().trim() || '';
};

export const selectAccessToken = (state: RootState) =>
  state.auth.tokenVerification?.accessToken;

export const selectIncidentGatheringId = (state: RootState) => state.auth.incidentGatheringId;
export const selectIncidentViewingId = (state: RootState) => state.auth.incidentViewingId;

export const selectIsStateUser = (state: RootState): boolean =>
  state.auth.tokenVerification?.pvActiveGroupID === 1;

export const selectIsCountyUser = (state: RootState): boolean => {
  const groupId = state.auth.tokenVerification?.pvActiveGroupID ?? 0;
  return groupId >= 2 && groupId <= 48 && groupId !== 42;
};

export const selectActiveCountyId = (state: RootState): number =>
  state.auth.activeCountyId ?? state.auth.tokenVerification?.pvActiveGroupID ?? 0;

export const selectIsGroupAdmin = (state: RootState): boolean => {
  const countyId = state.auth.activeCountyId ?? state.auth.tokenVerification?.pvActiveGroupID ?? 0;
  const currentGroups = state.auth.tokenVerification?.currentGroups || [];
  const group = currentGroups.find((g) => +g.i === +countyId);
  return group?.a === 1;
};

export const selectEdmaGroupPermission = (state: RootState): string | null => {
  const attributes = state.auth.tokenVerification?.attributes;
  const countyId = state.auth.activeCountyId ?? state.auth.tokenVerification?.pvActiveGroupID ?? 0;
  if (!attributes) return null;

  const edmaEntry = attributes.find((attr) => 'EDMA' in attr);
  if (!edmaEntry) return null;

  const edmaGroups = edmaEntry['EDMA'];
  const groupKey = String(countyId);
  const groupEntry = edmaGroups.find((item) => groupKey in item);
  if (!groupEntry) return null;

  const groupData = groupEntry[groupKey];
  if (!Array.isArray(groupData)) return null;

  const permEntry = groupData.find((item) => 'P' in item);
  if (!permEntry) return null;

  const perm = permEntry['P'];
  return Array.isArray(perm) && perm.length > 0 ? perm[0] : null;
};

export const selectCanSetGathering = (state: RootState): boolean => {
  const perm = selectEdmaGroupPermission(state);
  if (perm === 'A') return true;
  if (perm === 'U' || perm === 'G') return false;
  // No EDMA attribute permission — fallback to existing role checks
  return isAdministratorSelector(state) || selectIsGroupAdmin(state);
};
